// Remembers where an unauthenticated user was headed so we can send them there
// after they finish signing in. Because signIn() does a full-page redirect to
// WorkOS and back to /callback, this must survive a page reload — sessionStorage
// (per-tab, persists across reloads/redirects) is the right home for it.
const STORAGE_KEY = "postLoginRedirect";

// Auth surfaces themselves are never valid destinations — sending the user back
// to one after login would loop or dump them on a blank sign-in screen.
const IGNORED_PATHS = ["/login", "/initiate-login", "/callback"];

/**
 * Returns true only for safe in-app destinations: a path rooted at "/" but not
 * a protocol-relative "//host" URL (which would be an open-redirect), and not
 * one of the auth routes.
 */
function isSafeRedirectPath(path: string | null | undefined): path is string {
  if (!path || !path.startsWith("/") || path.startsWith("//")) return false;
  // Browsers normalize backslashes, control characters and dot segments.
  // Validate the resulting URL as well as its spelling before navigation.
  if (path.includes("\\") || Array.from(path).some((char) =>
    char.charCodeAt(0) <= 0x20 || char.charCodeAt(0) === 0x7f,
  )) return false;
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    const pathname = decodeURIComponent(url.pathname)
      .replace(/\/+$/, "")
      .toLowerCase();
    return !IGNORED_PATHS.includes(pathname);
  } catch {
    return false;
  }
}

export function savePostLoginRedirect(path: string): void {
  if (!isSafeRedirectPath(path)) return;
  // sessionStorage can throw (Safari private mode, storage disabled/full). A
  // failure here just means we fall back to the default post-login path.
  try {
    window.sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    // ignore — nothing stored, normal login flow continues
  }
}

/**
 * Reads and clears the stored destination. Single-use so a later, unrelated
 * login doesn't resurrect a stale target.
 */
export function consumePostLoginRedirect(): string | null {
  let path: string | null = null;
  try {
    path = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — fall back to the default post-login path
  }
  return isSafeRedirectPath(path) ? path : null;
}
